import React, { useCallback, useEffect, useState } from 'react';
import styles from './SubtitlesRightSidebar.module.scss';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../components/Button/Button';
import { Icon } from '../../../../components/Icon';
import { useHistory } from 'react-router-dom';
import LanguageSelect from '../LanguageSelect/LanguageSelect';
import SpeakersTab from '../SpeakersTab/SpeakersTab';
import SubtitlesLoadingComponent from '../SubtitlesLoadingComponent/SubtitlesLoadingComponent';
import SearchResultsControl from '../SearchResultsControl/SearchResultsControl';
import Spinner from '../../../../../common/components/Spinner';
import SubtitlesEditorComponent from '../SubtitlesEditorComponent/SubtitlesEditorComponent';
import { EDITOR_MODE } from '../../const';
import CloseButton from '../../../../components/CloseButton/CloseButton';

const searchResultsInit = { current: {}, total: [] };

const SubtitlesRightSidebar = ({
  supportedLanguages,
  mode,
  init,
  subtitles,
  availableLanguages,
  subtitlesLanguage,
  setSubtitlesLanguage,
  loading,
  estimatedTime,
  updateSubtitle,
  activeSubtitle,
  handleSeekToSubtitle,
  hasChanged,
  handleGenerateTranscription,
  handleCancelTranscription,
  currentLanguage,
  onLanguageChange,
  handleSave,
  video,
  appConfigLanguages,
  undoLastAction,
  redoLastAction,
  controlsDisabled,
  scenesLimits,
  listMode,
  setListMode
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [speakersMode, setSpeakersMode] = useState(0);
  const [searchResults, setSearchResults] = useState(searchResultsInit);
  const [searchValue, setSearchValue] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const handleSearchResult = value => {
    setSearchValue(value);
  };

  const nextSearchResult = () => {
    if (searchResults.current.index < searchResults.total.length - 1) {
      const nextValue = searchResults.total[searchResults.current.index + 1];
      setSearchResults(prevState => ({ ...prevState, current: nextValue }));
      handleSeekToSubtitle(nextValue);
    }
  };

  const previousSearchResult = () => {
    if (searchResults.current.index > 0) {
      const prevValue = searchResults.total[searchResults.current.index - 1];
      setSearchResults(prevState => ({ ...prevState, current: prevValue }));
      handleSeekToSubtitle(prevValue);
    }
  };

  const filterSearchResults = subtitles => {
    if (searchValue?.trim().length > 0 && Object.keys(subtitles).length > 0) {
      let filteredArray = [];
      for (const type in subtitles) {
        if (subtitles[type] !== null) {
          const subtitlesWithSearchValue = subtitles[type]
            ?.map(({ subtitles }) => subtitles
              .filter(item => item.hasOwnProperty('trimmedData'))
              .filter(({ trimmedData }) => trimmedData.text.toLowerCase().includes(searchValue.toLowerCase()))
              .map(({ trimmedData, key }, index) => ({ ...trimmedData, index, key }))).flat();
          subtitlesWithSearchValue?.length && filteredArray.push(...subtitlesWithSearchValue);
        }
      }
      setSearchResults({ current: filteredArray[0], total: filteredArray });
      filteredArray && handleSeekToSubtitle(filteredArray[0]);
    } else {
      setSearchResults(searchResultsInit);
    }
  };

  const onSearchClose = () => {
    setSearchValue('');
    setSearchResults(searchResultsInit);
  };

  const saveSubtitles = async () => {
    setSaveLoading(true);
    await handleSave();
    setSaveLoading(false);
  };

  useEffect(() => {
    filterSearchResults(subtitles);
  }, [searchValue, currentLanguage, subtitles]);

  const handleClose = useCallback(() => {
    if (hasChanged) {
      const confirmClose = confirm(t('subtitles.editor.user_warning'));
      if (confirmClose) {
        history.goBack();
      }
    } else {
      history.goBack();
    }
  }, [hasChanged]);

  useEffect(() => {
    if (loading) {
      setSearchValue('');
    }
  }, [loading]);

  if (!init) {
    return (
      <div className={styles.sidebarWrapper}>
        <Spinner width="40px" height="40px" size="2px" className={styles.initSpinner} />
      </div>
    );
  }

  return (
    <div className={styles.sidebarWrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          {searchResults.total.length === 0 ? (
            <h1>{t('subtitles.editor.title')}</h1>
          ) : (
            <SearchResultsControl
              current={searchResults.current}
              total={searchResults.total}
              handleNext={nextSearchResult}
              handlePrevious={previousSearchResult}
            />
          )}
          <CloseButton onClose={handleClose} />
        </div>
        {loading && (
          <SubtitlesLoadingComponent
            includeCancelButton={loading.includeCancelButton}
            onCancel={handleCancelTranscription}
            estimatedTime={estimatedTime}
            header={loading.header}
            textLines={loading.textLines}
          />
        )}
        {!loading && (
          <>
            {mode === EDITOR_MODE.GENERATE_SUBTITLES_MODE && (
              <div>
                <p className={styles.selectText}>{t('subtitles.editor.choose_language')}</p>
                <LanguageSelect
                  title={t('subtitles.editor.language_select')}
                  options={supportedLanguages}
                  onSelect={setSubtitlesLanguage}
                  currentlySelected={subtitlesLanguage}
                />
                <div className={styles.speakersContainer}>
                  <h2 className={styles.speakersTitle}>{t('subtitles.editor.speakers_mode.title')}</h2>
                  <div className={styles.modeTabContainer}>
                    <SpeakersTab mode={0} currentMode={speakersMode} onSelect={setSpeakersMode} />
                    <SpeakersTab mode={1} currentMode={speakersMode} onSelect={setSpeakersMode} />
                  </div>
                </div>
                <CustomButton
                  label={t('subtitles.editor.generate_transcription')}
                  onClick={handleGenerateTranscription}
                  variant="primary"
                />
              </div>
            )}
            {(mode === EDITOR_MODE.EDIT_SUBTITLES_MODE || mode === EDITOR_MODE.TRANSLATE_SUBTITLES_MODE) && (
              <SubtitlesEditorComponent
                mode={mode}
                availableLanguages={availableLanguages}
                subtitlesLanguage={subtitlesLanguage}
                subtitles={subtitles}
                updateSubtitle={updateSubtitle}
                activeSubtitle={activeSubtitle}
                searchValue={searchValue}
                onSearch={handleSearchResult}
                video={video}
                appConfigLanguages={appConfigLanguages}
                currentSearchResult={searchResults.current?.text}
                currentLanguage={currentLanguage}
                onLanguageChange={onLanguageChange}
                handleSeekToSubtitle={handleSeekToSubtitle}
                onSearchClose={onSearchClose}
                scenesLimits={scenesLimits}
                listMode={listMode}
                setListMode={setListMode}
              />
            )}
          </>
        )}
      </div>
      {!(loading || mode === EDITOR_MODE.GENERATE_SUBTITLES_MODE) && (
        <div className={styles.btnContainer}>
          <div className={styles.undoRedoContainer}>
            <CustomButton
              variant="outline"
              className={styles.undoRedoBtn}
              onClick={undoLastAction}
              disabled={controlsDisabled.undo || saveLoading}
            >
              <Icon src="undo" className={styles.checkIcon} />
              <span className={styles.undoRedoText}>{t('my_video.tools.trim.undo')}</span>
            </CustomButton>
            <CustomButton
              variant="outline"
              className={styles.undoRedoBtn}
              onClick={redoLastAction}
              disabled={controlsDisabled.redo || saveLoading}
            >
              <Icon src="redo" className={styles.checkIcon} />
              <span>{t('my_video.tools.trim.redo')}</span>
            </CustomButton>
          </div>
          <CustomButton
            variant="primary"
            className={styles.saveButton}
            onClick={saveSubtitles}
            disabled={!hasChanged}
            loading={saveLoading}
          >
            <Icon src="installed" className={styles.checkIcon} />
            <span className={styles.undoRedoText}>{t('general.editor.save_changes')}</span>
          </CustomButton>
        </div>
      )}
    </div>
  );
};

export default SubtitlesRightSidebar;
